import Carousel from '@carvana/ds-carousel';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import TrackClick from '@carvana/merch-components/TrackClick';
import { NavPayloadAction } from '@carvana/merch-native';
import cx from 'classnames';
import { useContext } from 'react';
import { INTERACTION_TO_ACTION } from '~/constants/common/CarouselAnalytics';
import Sections from '~/constants/common/sections';
import { useIsMobile } from '~/context/common/DeviceContext';
import useGetConsumerHref from '~/hooks/common/selectors/useGetConsumerHref';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import optimizeImage from '~/utilities/optimizeImage';
import popularModels from './popularModels';
import {
  BrandText,
  ModelImage,
  ModelText,
  SectionContainer,
  TextContainer,
  TileContainer
} from './ShopPopularModels.styles';
import SectionWrapper from '../SectionWrapper';

type OwnProps = {
  hasBackground?: boolean;
  hasCompactSpacing?: boolean;
  whiteBg?: boolean;
};

function ShopPopularModels({
  hasBackground = true,
  hasCompactSpacing = false,
  whiteBg = false
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const getHref = useGetConsumerHref();
  const isMobile = useIsMobile();

  return (
    <SectionWrapper
      analyticsSection="Shop Popular Models"
      eventName="Content Section"
      className={cx({ 'pb-2xl sm:pb-3xl md:pb-7xl': hasCompactSpacing })}
      hasBackground={hasBackground}
      hasBottomSpacing={!hasCompactSpacing}
    >
      <SectionContainer>
        <Carousel
          ariaLabel="Shop Popular Models"
          data-testid="shop-popular-models-carousel"
          classes={{
            arrows: 'sm:flex sm:gap-lg',
            slide:
              'pl-xl first:pl-2xl last:pr-2xl sm:pl-2xl sm:first:pl-4xl sm:last:pr-4xl md:first:pl-3xl md:last:pr-3xl lg:pl-0 lg:first:pl-0 lg:last:pr-0 pb-[2px]',
            slides: 'gap-0 lg:gap-2xl',
            topSection:
              'px-2xl sm:px-4xl md:px-3xl gap-0 lg:px-0 sm:grid-cols-[1fr_auto]'
          }}
          hidePagination={!isMobile}
          headerContent={
            <h2 className="text-text-strong t-heading-lg sm:mb-sm md:t-heading-xl">
              Shop popular models
            </h2>
          }
          hideScrollBar
          slideByVisible
          trackEvent={event => {
            trackEvent('Shop Popular Models', {
              ...event,
              action: INTERACTION_TO_ACTION[event.interactionType]
            });
          }}
        >
          {popularModels.map(model => (
            <TrackClick
              key={model.url}
              element="a"
              href={getHref(model.url)}
              data-analytics-id="Home feed card"
              data-analytics-attributes={{
                description: 'Popular model tile',
                content: `${model.make} ${model.model}`
              }}
              data-testid="popular-model"
              data-native-payload={{
                action: NavPayloadAction.ViewSrp
              }}
            >
              <TileContainer whiteBg={whiteBg}>
                <TextContainer>
                  <BrandText>{model.make}</BrandText>
                  <ModelText>{model.model}</ModelText>
                </TextContainer>

                <ModelImage
                  src={optimizeImage(model.image, {
                    quality: 90,
                    format: 'auto'
                  })}
                  alt=""
                  loading="lazy"
                />
              </TileContainer>
            </TrackClick>
          ))}
        </Carousel>
      </SectionContainer>
    </SectionWrapper>
  );
}

export default withErrorBoundary(
  ShopPopularModels,
  undefined,
  undefined,
  Sections.PopularModels
);
